import { DataPlaneResourcePathPrefix } from '../constants/data-plane';
import { StringifiedPattern } from '../constants/patterns';
import { isValidDataPlaneUri, parseDataPlanePath } from '../ids/data-plane';
import { ClientError } from '../models/common';
import { DataPlaneIdTokenSet } from '../models/data-plane';
import { isUndefinedOrWhiteSpace } from '../utilities/string';
import { tryOrDefault } from '../utilities/try-or-default';

/* eslint-disable security/detect-non-literal-regexp */
// Justification: regex is non-literal, but is not constructed from user input.
const environmentOperationPathPattern = new RegExp(
    `^/${DataPlaneResourcePathPrefix.Project}/${StringifiedPattern.ResourceName}/${DataPlaneResourcePathPrefix.User}/${StringifiedPattern.Guid}/${DataPlaneResourcePathPrefix.Environment}/${StringifiedPattern.ResourceName}/${DataPlaneResourcePathPrefix.EnvironmentOperation}/${StringifiedPattern.Guid}$`,
    'i'
);
/* eslint-enable security/detect-non-literal-regexp */

export const createEnvironmentOperationDataPlaneUri = (
    tokens: DataPlaneIdTokenSet<'devCenter' | 'environmentName' | 'environmentOperationId' | 'projectName' | 'user'>
): string => {
    const { devCenter, environmentName, environmentOperationId, projectName, user } = tokens;

    if (isUndefinedOrWhiteSpace(devCenter)) {
        throw new ClientError('Dev center token cannot be undefined or white space');
    }

    if (isUndefinedOrWhiteSpace(environmentName)) {
        throw new ClientError('Environment name token cannot be undefined or white space');
    }

    if (isUndefinedOrWhiteSpace(environmentOperationId)) {
        throw new ClientError('Environment operation ID token cannot be undefined or white space');
    }

    if (isUndefinedOrWhiteSpace(projectName)) {
        throw new ClientError('Project name token cannot be undefined or white space');
    }

    if (isUndefinedOrWhiteSpace(user)) {
        throw new ClientError('User token cannot be undefined or white space');
    }

    const path = `/${[
        DataPlaneResourcePathPrefix.Project,
        projectName.toLowerCase(),
        DataPlaneResourcePathPrefix.User,
        user.toLowerCase(),
        DataPlaneResourcePathPrefix.Environment,
        environmentName.toLowerCase(),
        DataPlaneResourcePathPrefix.EnvironmentOperation,
        environmentOperationId.toLowerCase(),
    ].join('/')}`;

    const uri = new URL(path, devCenter);

    return `${uri}`;
};

export const getTokensFromEnvironmentOperationDataPlaneUri = (
    uri: string
): DataPlaneIdTokenSet<'devCenter' | 'environmentName' | 'environmentOperationId' | 'projectName' | 'user'> => {
    if (!isValidEnvironmentOperationDataPlaneUri(uri)) {
        throw new ClientError('Given URI was not a valid Data Plane URI for an environment operation');
    }

    const { origin, pathname } = new URL(uri);
    const [, projectName, , user, , environmentName, , environmentOperationId] = parseDataPlanePath(pathname);

    return {
        devCenter: origin,
        environmentName,
        environmentOperationId,
        projectName,
        user,
    };
};

export const isValidEnvironmentOperationDataPlaneUri = (uri: string): boolean =>
    isValidDataPlaneUri(uri, environmentOperationPathPattern);

export const tryCreateEnvironmentOperationDataPlaneUri = tryOrDefault(createEnvironmentOperationDataPlaneUri);

export const tryGetTokensFromEnvironmentOperationDataPlaneUri = tryOrDefault(
    getTokensFromEnvironmentOperationDataPlaneUri
);
